/**
 * Session Storage Utilities for ChatBot
 */

import { ChatMessage } from '../types';
import { STORAGE_KEYS } from '../config';

/**
 * Save messages to session storage
 */
export const saveMessages = (messages: ChatMessage[]): void => {
  try {
    sessionStorage.setItem(STORAGE_KEYS.messages, JSON.stringify(messages));
  } catch (e) {
    console.warn('Failed to save messages to storage', e);
  }
};

/**
 * Load messages from session storage
 */
export const loadMessages = (): ChatMessage[] => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEYS.messages);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert timestamp strings back to Date objects
      return parsed.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      }));
    }
  } catch (e) {
    console.warn('Failed to load messages from storage', e);
  }
  return [];
};

/**
 * Clear all messages from session storage
 */
export const clearMessages = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.messages);
};

/**
 * Save chatbot open state
 */
export const saveChatBotOpenState = (isOpen: boolean): void => {
  sessionStorage.setItem(STORAGE_KEYS.isOpen, String(isOpen));
};

/**
 * Load chatbot open state
 */
export const loadChatBotOpenState = (): boolean => {
  const stored = sessionStorage.getItem(STORAGE_KEYS.isOpen);
  return stored === 'true';
};

/**
 * Save chatbot session readiness (server-side session has been started)
 */
export const saveChatSessionReadyState = (isReady: boolean): void => {
  sessionStorage.setItem(STORAGE_KEYS.sessionReady, String(isReady));
};

/**
 * Load chatbot session readiness
 */
export const loadChatSessionReadyState = (): boolean => {
  const stored = sessionStorage.getItem(STORAGE_KEYS.sessionReady);
  return stored === 'true';
};

/**
 * Clear chatbot session readiness
 */
export const clearChatSessionReadyState = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.sessionReady);
};

/**
 * Clear all chatbot data from session storage
 */
export const clearAllChatBotData = (): void => {
  clearMessages();
  sessionStorage.removeItem(STORAGE_KEYS.isOpen);
  clearChatSessionReadyState();
};
